import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Login.css'; // Reuse login styles for now
import { API_BASE_URL } from '../utils/apiConfig';
import { formatCurrency } from '../utils/numberingSystem';

const emptyState = {
  company: {
    name: '',
    address: '',
    contactNumbers: [''],
    websites: [''],
    gst: '',
    logo: null
  },
  products: [],
  customers: [],
  users: []
};

export default function Register() {
  const [stage, setStage] = useState(1);
  const [data, setData] = useState(emptyState);
  const [productDraft, setProductDraft] = useState({ name: '', description: '', price: '', taxRate: '', logo: null });
  const [customerDraft, setCustomerDraft] = useState({ name: '', addressLine1: '', addressLine2: '', locality: '', city: '', state: '', pincode: '', phone: '', gst: '' });
  const [userDraft, setUserDraft] = useState({ name: '', email: '', password: '', role: 'user' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successId, setSuccessId] = useState(null);

  const formatProductPrice = (value) => formatCurrency(Number(value) || 0, 'indian', { prefix: 'Rs.' });

  useEffect(() => {
    setStatus(null);
  }, [stage]);

  function update(path, value) {
    setData((d) => {
      const next = { ...d };
      const [section, key] = path.split('.');
      next[section] = { ...next[section], [key]: value };
      return next;
    });
  }

  function updateCompanyContactNumber(index, value) {
    setData((d) => {
      const next = { ...d };
      const contactNumbers = [...(next.company.contactNumbers || [])];
      contactNumbers[index] = value;
      next.company = { ...next.company, contactNumbers };
      return next;
    });
  }

  function addCompanyContactNumber() {
    setData((d) => ({
      ...d,
      company: {
        ...d.company,
        contactNumbers: [...(d.company.contactNumbers || []), '']
      }
    }));
  }

  function removeCompanyContactNumber(index) {
    setData((d) => {
      const contactNumbers = (d.company.contactNumbers || []).filter((_, i) => i !== index);
      return {
        ...d,
        company: {
          ...d.company,
          contactNumbers: contactNumbers.length > 0 ? contactNumbers : ['']
        }
      };
    });
  }

  function updateCompanyWebsite(index, value) {
    setData((d) => {
      const next = { ...d };
      const websites = [...(next.company.websites || [])];
      websites[index] = value;
      next.company = { ...next.company, websites };
      return next;
    });
  }

  function addCompanyWebsite() {
    setData((d) => ({
      ...d,
      company: {
        ...d.company,
        websites: [...(d.company.websites || []), '']
      }
    }));
  }

  function removeCompanyWebsite(index) {
    setData((d) => {
      const websites = (d.company.websites || []).filter((_, i) => i !== index);
      return {
        ...d,
        company: {
          ...d.company,
          websites: websites.length > 0 ? websites : ['']
        }
      };
    });
  }

  function handleFile(section, file) {
    if (section === 'productDraft') {
      setProductDraft((p) => ({ ...p, logo: file }));
    } else {
      update(`${section}.logo`, file);
    }
  }

  function updateDraft(key, value) {
    setProductDraft((p) => ({ ...p, [key]: value }));
  }

  function updateCustomerDraft(key, value) {
    setCustomerDraft((c) => ({ ...c, [key]: value }));
  }

  function updateUserDraft(key, value) {
    setUserDraft((u) => ({ ...u, [key]: value }));
  }

  function addProduct() {
    if (!productDraft.name.trim() || !productDraft.price.toString().trim()) {
      setStatus('Please provide product name and price before adding.');
      return;
    }
    setData((d) => ({ ...d, products: [...d.products, productDraft] }));
    setProductDraft({ name: '', description: '', price: '', taxRate: '', logo: null });
    setStatus(null);
  }

  function addCustomer() {
    if (!customerDraft.name.trim() || !customerDraft.addressLine1.trim() || !customerDraft.city.trim() || !customerDraft.state.trim()) {
      setStatus('Please provide customer name, address line 1, city and state before adding.');
      return;
    }
    setData((d) => ({ ...d, customers: [...d.customers, customerDraft] }));
    setCustomerDraft({ name: '', addressLine1: '', addressLine2: '', locality: '', city: '', state: '', pincode: '', phone: '', gst: '' });
    setStatus(null);
  }

  function addUser() {
    if (!userDraft.name.trim() || !userDraft.email.trim() || !userDraft.password) {
      setStatus('Please provide user name, email and password before adding.');
      return;
    }
    // Enforce single appAdmin on client-side
    if (userDraft.role === 'appAdmin') {
      const hasAppAdmin = (data.users || []).some((u) => u.role === 'appAdmin');
      if (hasAppAdmin) {
        setStatus('An appAdmin already exists — only one appAdmin is allowed.');
        return;
      }
    }
    setData((d) => ({ ...d, users: [...d.users, userDraft] }));
    setUserDraft({ name: '', email: '', password: '', role: 'user' });
    setStatus(null);
  }

  function removeUser(index) {
    setData((d) => ({ ...d, users: d.users.filter((_, i) => i !== index) }));
  }

  function removeCustomer(index) {
    setData((d) => ({ ...d, customers: d.customers.filter((_, i) => i !== index) }));
  }

  function removeProduct(index) {
    setData((d) => ({ ...d, products: d.products.filter((_, i) => i !== index) }));
  }

  function canNext() {
    if (stage === 1) {
      const c = data.company;
      const contactNumbers = Array.isArray(c.contactNumbers) ? c.contactNumbers : [];
      const hasContactNumber = contactNumbers.some((value) => String(value || '').trim());
      return c.name.trim() && c.address.trim() && hasContactNumber;
    }
    if (stage === 2) {
      // Products are optional for onboarding
      return true;
    }
    if (stage === 3) {
      // Customers are optional for onboarding
      return true;
    }
    if (stage === 4) {
      // Users stage: ensure exactly one appAdmin and at least one user
      if (Array.isArray(data.users)) {
        const appAdminCount = data.users.reduce((acc, u) => acc + (u.role === 'appAdmin' ? 1 : 0), 0);
        return appAdminCount === 1 && data.users.length > 0;
      }
      return false;
    }
    return true;
  }

  function next() {
    if (!canNext()) {
      if (stage === 4) {
        setStatus('Please add at least one user with the AppAdmin role before continuing.');
        return;
      }
      setStatus('Please complete the required fields before continuing.');
      return;
    }
    setStage((s) => Math.min(5, s + 1));
  }

  function prev() {
    setStage((s) => Math.max(1, s - 1));
  }

  async function fileToDataUrl(file) {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('Submitting...');
    setLoading(true);

    try {
      // Convert logo and product logos to base64 data URLs
      const companyLogo = await fileToDataUrl(data.company.logo);
      const products = await Promise.all(
        (data.products || []).map(async (p) => ({
          name: p.name || '',
          description: p.description || '',
          price: p.price ? Number(p.price) : 0,
          taxRate: p.taxRate ? Number(p.taxRate) : 0,
          logo: await fileToDataUrl(p.logo)
        }))
      );

      const payload = {
        name: data.company.name,
        contactEmail: '',
        contactName: '',
        phone: Array.isArray(data.company.contactNumbers)
          ? String(data.company.contactNumbers.find((value) => String(value || '').trim()) || '')
          : '',
        contactNumbers: Array.isArray(data.company.contactNumbers)
          ? data.company.contactNumbers.map((value) => String(value || '').trim()).filter(Boolean)
          : [],
        websites: Array.isArray(data.company.websites)
          ? data.company.websites.map((value) => String(value || '').trim()).filter(Boolean)
          : [],
        address: data.company.address,
        gst: data.company.gst,
        logo: companyLogo,
        products,
        customers: data.customers.map((c) => ({
          name: c.name,
          addressLine1: c.addressLine1,
          addressLine2: c.addressLine2,
          locality: c.locality,
          city: c.city,
          state: c.state,
          pincode: c.pincode,
          phone: c.phone,
          gst: c.gst
        })),
        users: (data.users || []).map((u) => ({
          name: u.name,
          email: u.email,
          password: u.password,
          role: u.role
        })),
        metadata: { onboardedFrom: 'frontend' }
      };

      const response = await axios.post(`${API_BASE_URL}/organisations`, payload);
      setSuccessId(response.data.id || null);
      setShowSuccess(true);
      setStatus(null);

      // Redirect to home after showing splash
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Onboarding error:', error);
      setStatus(error.response?.data?.error || error.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '32px', color: '#1f2937', fontSize: '32px' }}>
        Onboard your business
      </h1>

      {/* Enhanced Wizard steps indicator */}
      <div className="wizard-steps" style={{ display: 'flex', gap: 12, marginBottom: 32, justifyContent: 'center' }}>
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: stage === s ? '#2563eb' : stage > s ? '#10b981' : '#e5e7eb',
                color: stage === s || stage > s ? '#fff' : '#6b7280',
                fontSize: '13px',
                fontWeight: stage === s ? '600' : 'normal',
                minWidth: '100px',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                boxShadow: stage === s ? '0 2px 8px rgba(37, 99, 235, 0.3)' : 'none',
                cursor: 'pointer'
              }}
            >
              {s === 1 ? 'Company' : s === 2 ? 'Products' : s === 3 ? 'Customers' : s === 4 ? 'Users' : 'Review'}
            </div>
            {s < 5 && (
              <div
                style={{
                  width: '24px',
                  height: '2px',
                  background: stage > s ? '#10b981' : '#d1d5db',
                  transition: 'background 0.3s ease'
                }}
              />
            )}
          </div>
        ))}
      </div>

      <form className="register-form" onSubmit={handleSubmit}>
        {/* Stage 1: Company Information */}
        {stage === 1 && (
          <fieldset style={{ border: 'none', padding: 0, marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Company Information
            </h2>
            <div style={{ display: 'grid', gap: '20px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Company name</span>
                <input
                  value={data.company.name}
                  onChange={(e) => update('company.name', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  required
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Address</span>
                <input
                  value={data.company.address}
                  onChange={(e) => update('company.address', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  required
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Contact numbers</span>
                {(data.company.contactNumbers || []).map((value, index) => (
                  <div key={`company-contact-${index}`} style={{ display: 'flex', gap: '8px' }}>
                    <input
                      value={value}
                      onChange={(e) => updateCompanyContactNumber(index, e.target.value)}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        flex: 1
                      }}
                      required={index === 0}
                    />
                    <button
                      type="button"
                      onClick={() => removeCompanyContactNumber(index)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #fecaca',
                        background: '#fee2e2',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCompanyContactNumber}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #bfdbfe',
                    background: '#eff6ff',
                    color: '#2563eb',
                    cursor: 'pointer',
                    alignSelf: 'flex-start'
                  }}
                >
                  Add contact number
                </button>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Websites (optional)</span>
                {(data.company.websites || []).map((value, index) => (
                  <div key={`company-website-${index}`} style={{ display: 'flex', gap: '8px' }}>
                    <input
                      value={value}
                      onChange={(e) => updateCompanyWebsite(index, e.target.value)}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        flex: 1
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeCompanyWebsite(index)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #fecaca',
                        background: '#fee2e2',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCompanyWebsite}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #bfdbfe',
                    background: '#eff6ff',
                    color: '#2563eb',
                    cursor: 'pointer',
                    alignSelf: 'flex-start'
                  }}
                >
                  Add website
                </button>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>GST number (optional)</span>
                <input
                  value={data.company.gst}
                  onChange={(e) => update('company.gst', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Company logo (optional)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFile('company', e.target.files[0] || null)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </label>
            </div>
          </fieldset>
        )}

        {/* Stage 2: Products */}
        {stage === 2 && (
          <fieldset style={{ border: 'none', padding: 0, marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Product Information
            </h2>
            <div style={{ display: 'grid', gap: '20px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Product name</span>
                <input
                  value={productDraft.name}
                  onChange={(e) => updateDraft('name', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Description (optional)</span>
                <input
                  value={productDraft.description}
                  onChange={(e) => updateDraft('description', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Price</span>
                <input
                  value={productDraft.price}
                  onChange={(e) => updateDraft('price', e.target.value)}
                  type="number"
                  step="0.01"
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Tax rate (%) (optional)</span>
                <input
                  value={productDraft.taxRate}
                  onChange={(e) => updateDraft('taxRate', e.target.value)}
                  type="number"
                  step="0.01"
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Product logo (optional)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFile('productDraft', e.target.files[0] || null)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '12px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addProduct}
                  style={{
                    padding: '10px 20px',
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Add product
                </button>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  {data.products.length} product(s) added
                </div>
              </div>

              {data.products.length > 0 && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ marginBottom: '12px', color: '#1f2937', fontSize: '14px', fontWeight: '600' }}>
                    Added products
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.products.map((p, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'center',
                          padding: '12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#1f2937' }}>
                            {p.name}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '13px' }}>
                            {formatProductPrice(p.price)} {p.taxRate ? `(${p.taxRate}% tax)` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => removeProduct(i)}
                          style={{
                            padding: '6px 12px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Stage 3: Customers */}
        {stage === 3 && (
          <fieldset style={{ border: 'none', padding: 0, marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Customer Information
            </h2>
            <div style={{ display: 'grid', gap: '20px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Customer name</span>
                <input
                  value={customerDraft.name}
                  onChange={(e) => updateCustomerDraft('name', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Address Line 1</span>
                <input
                  value={customerDraft.addressLine1}
                  onChange={(e) => updateCustomerDraft('addressLine1', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Address Line 2 (optional)</span>
                <input
                  value={customerDraft.addressLine2}
                  onChange={(e) => updateCustomerDraft('addressLine2', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Locality (optional)</span>
                <input
                  value={customerDraft.locality}
                  onChange={(e) => updateCustomerDraft('locality', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>City</span>
                <input
                  value={customerDraft.city}
                  onChange={(e) => updateCustomerDraft('city', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>State</span>
                <input
                  value={customerDraft.state}
                  onChange={(e) => updateCustomerDraft('state', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Pincode (optional)</span>
                <input
                  value={customerDraft.pincode}
                  onChange={(e) => updateCustomerDraft('pincode', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Phone number (optional)</span>
                <input
                  value={customerDraft.phone}
                  onChange={(e) => updateCustomerDraft('phone', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>GST number (optional)</span>
                <input
                  value={customerDraft.gst}
                  onChange={(e) => updateCustomerDraft('gst', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '12px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addCustomer}
                  style={{
                    padding: '10px 20px',
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Add customer
                </button>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  {data.customers.length} customer(s) added
                </div>
              </div>

              {data.customers.length > 0 && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ marginBottom: '12px', color: '#1f2937', fontSize: '14px', fontWeight: '600' }}>
                    Added customers
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.customers.map((c, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'center',
                          padding: '12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#1f2937' }}>
                            {c.name}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '13px' }}>
                            {[c.city, c.state].filter(Boolean).join(', ')} {c.phone ? `— ${c.phone}` : ''} {c.gst ? `— GST: ${c.gst}` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => removeCustomer(i)}
                          style={{
                            padding: '6px 12px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Stage 4: Users */}
        {stage === 4 && (
          <fieldset style={{ border: 'none', padding: 0, marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Users
            </h2>
            <div style={{ marginBottom: '16px', color: '#6b7280', fontSize: '13px' }}>
              Note: Add at least one user with the App Admin role to manage settings and access admin tools.
            </div>
            <div style={{ display: 'grid', gap: '20px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Name</span>
                <input
                  value={userDraft.name}
                  onChange={(e) => updateUserDraft('name', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Email</span>
                <input
                  type="email"
                  value={userDraft.email}
                  onChange={(e) => updateUserDraft('email', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Password</span>
                <input
                  type="password"
                  value={userDraft.password}
                  onChange={(e) => updateUserDraft('password', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151' }}>
                <span style={{ fontWeight: '500' }}>Role</span>
                <select
                  value={userDraft.role}
                  onChange={(e) => updateUserDraft('role', e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="user">User</option>
                  <option value="claimsUser">Claims User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="appAdmin">App Admin</option>
                </select>
              </label>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '12px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addUser}
                  style={{
                    padding: '10px 20px',
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Add user
                </button>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  {data.users.length} user(s) added
                </div>
              </div>

              {data.users.length > 0 && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ marginBottom: '12px', color: '#1f2937', fontSize: '14px', fontWeight: '600' }}>
                    Added users
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {data.users.map((u, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'center',
                          padding: '12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#1f2937' }}>
                            {u.name}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '13px' }}>
                            {u.email} — {u.role}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => removeUser(i)}
                          style={{
                            padding: '6px 12px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Stage 5: Review & Submit */}
        {stage === 5 && (
          <fieldset style={{ border: 'none', padding: 0, marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Review & Submit
            </h2>
            <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                  Company
                </h3>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>Name</span>
                      <div style={{ color: '#1f2937', fontWeight: '500' }}>{data.company.name}</div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>Address</span>
                      <div style={{ color: '#1f2937', fontWeight: '500' }}>{data.company.address}</div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>Phone</span>
                      <div style={{ color: '#1f2937', fontWeight: '500' }}>
                        {(data.company.contactNumbers || []).map((value) => String(value || '').trim()).filter(Boolean).join(', ') || 'N/A'}
                      </div>
                    </div>
                    {((data.company.websites || []).map((value) => String(value || '').trim()).filter(Boolean).length > 0) && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>Websites</span>
                        <div style={{ color: '#1f2937', fontWeight: '500' }}>
                          {(data.company.websites || []).map((value) => String(value || '').trim()).filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                    {data.company.gst && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>GST</span>
                        <div style={{ color: '#1f2937', fontWeight: '500' }}>{data.company.gst}</div>
                      </div>
                    )}
                  </div>
                  <div>
                    {data.company.logo ? (
                      <img
                        src={URL.createObjectURL(data.company.logo)}
                        alt="company logo"
                        style={{ height: 80, borderRadius: '8px', border: '1px solid #d1d5db' }}
                      />
                    ) : (
                      <div
                        style={{
                          height: 80,
                          width: 80,
                          background: '#e5e7eb',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}
                      >
                        No logo
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px', paddingTop: '16px', borderTop: '1px solid #d1d5db' }}>
                <h3 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                  Products ({data.products.length})
                </h3>
                {data.products.length === 0 ? (
                  <div style={{ color: '#6b7280' }}>No products added.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', borderBottom: '1px solid #d1d5db', padding: '8px 0', color: '#6b7280', fontSize: '12px', fontWeight: '600' }}>
                          Name
                        </th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #d1d5db', padding: '8px 0', color: '#6b7280', fontSize: '12px', fontWeight: '600' }}>
                          Price
                        </th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #d1d5db', padding: '8px 0', color: '#6b7280', fontSize: '12px', fontWeight: '600' }}>
                          Tax
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.products.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '10px 0', color: '#1f2937' }}>{p.name}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#1f2937' }}>{formatProductPrice(p.price)}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#1f2937' }}>{p.taxRate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={{ marginBottom: '24px', paddingTop: '16px', borderTop: '1px solid #d1d5db' }}>
                <h3 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                  Customers ({data.customers.length})
                </h3>
                {data.customers.length === 0 ? (
                  <div style={{ color: '#6b7280' }}>No customers added.</div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {data.customers.map((c, i) => (
                      <li key={i} style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>{c.name}</div>
                        <div style={{ color: '#6b7280', fontSize: '13px' }}>
                          {[c.city, c.state].filter(Boolean).join(', ')} {c.phone ? `— ${c.phone}` : ''} {c.gst ? `— GST: ${c.gst}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div style={{ paddingTop: '16px', borderTop: '1px solid #d1d5db' }}>
                <h3 style={{ marginBottom: '16px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                  Users ({data.users.length})
                </h3>
                {data.users.length === 0 ? (
                  <div style={{ color: '#6b7280' }}>No users added.</div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {data.users.map((u, i) => (
                      <li key={i} style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>{u.name}</div>
                        <div style={{ color: '#6b7280', fontSize: '13px' }}>
                          {u.email} — {u.role === 'appAdmin' ? '👤 App Admin' : u.role === 'admin' ? '👤 Admin' : u.role === 'manager' ? '📄 Manager' : u.role === 'claimsUser' ? '🧾 Claims User' : 'User'}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </fieldset>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '32px', justifyContent: 'space-between' }}>
          <button
            type="button"
            className="btn"
            onClick={prev}
            disabled={stage === 1}
            style={{
              padding: '10px 24px',
              background: stage === 1 ? '#e5e7eb' : '#f3f4f6',
              color: stage === 1 ? '#9ca3af' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontWeight: '500',
              cursor: stage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }} />
          {stage < 5 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={next}
              disabled={!canNext()}
              style={{
                padding: '10px 24px',
                background: canNext() ? '#2563eb' : '#bfdbfe',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: canNext() ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
            >
              Next →
            </button>
          )}
          {(stage === 2 || stage === 3) && (
            <button
              type="button"
              className="btn"
              onClick={next}
              style={{
                padding: '10px 20px',
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Skip for now
            </button>
          )}
          {stage === 5 && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{
                padding: '10px 32px',
                background: loading ? '#bfdbfe' : '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? 'Submitting...' : '✓ Complete'}
            </button>
          )}
        </div>
      </form>

      {status && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            borderRadius: '8px',
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #fca5a5',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {status}
        </div>
      )}

      {/* Success splash screen */}
      {showSuccess && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '32px',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              minWidth: '380px',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
            <h2 style={{ marginTop: 0, marginBottom: '12px', color: '#10b981', fontSize: '24px', fontWeight: '600' }}>
              Onboarding complete!
            </h2>
            <div style={{ marginBottom: '12px', color: '#374151', fontSize: '15px' }}>
              Organisation created {successId ? `— id: ${successId}` : ''}
            </div>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Redirecting to app…
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
